import { apiClient, fetcher } from './apiClient';

type Member = {
    id: string;
    name: string;
    email: string;
    roles: string[];
    directPermissions: string[];
    permissions: string[];
};

type AssignableRole = {
    name: string;
    permissions: string[];
};

const jsonHeaders = (csrfToken: string | null) => ({
    'Content-Type': 'application/json',
    'X-XSRF-TOKEN': csrfToken ?? '',
});

const listMembers = async (): Promise<Member[]> =>
    fetcher<Member[]>('api/organization/members');

const listAssignableRoles = async (): Promise<AssignableRole[]> =>
    fetcher<AssignableRole[]>('api/organization/assignable-roles');

/**
 * Provision a member in the active org. There are no email invites yet, so this
 * creates the user directly; they sign in via SSO, which matches on email.
 * Super admins only — the backend enforces it.
 */
const createMember = async (
    csrfToken: string | null,
    payload: { name: string; email: string; role: string },
): Promise<Member | undefined> => {
    if (csrfToken === null) return;
    const response = await apiClient.post(
        'api/organization/members',
        JSON.stringify(payload),
        { headers: jsonHeaders(csrfToken) },
    );

    return JSON.parse(response.data) as Member;
};

/**
 * Replace the member's tenant role. The API swaps the previous tenant role
 * rather than stacking; platform roles on Watchly staff are preserved.
 */
const assignRole = async (
    csrfToken: string | null,
    userId: string,
    role: string,
): Promise<void> => {
    if (csrfToken === null) return;
    await apiClient.post(
        `api/organization/members/${userId}/roles`,
        JSON.stringify({ role }),
        { headers: jsonHeaders(csrfToken) },
    );
};

const grantPermission = async (
    csrfToken: string | null,
    userId: string,
    permission: string,
): Promise<void> => {
    if (csrfToken === null) return;
    await apiClient.post(
        `api/organization/members/${userId}/permissions`,
        JSON.stringify({ permission }),
        { headers: jsonHeaders(csrfToken) },
    );
};

const revokePermission = async (
    csrfToken: string | null,
    userId: string,
    permission: string,
): Promise<void> => {
    if (csrfToken === null) return;
    await apiClient.delete(
        `api/organization/members/${userId}/permissions/${encodeURIComponent(permission)}`,
        { headers: jsonHeaders(csrfToken) },
    );
};

export {
    listMembers,
    listAssignableRoles,
    createMember,
    assignRole,
    grantPermission,
    revokePermission,
};
export type { AssignableRole, Member };
